<template>
	<NuxtLayout
		name="hero-diagonal"
		:background-video="{
			mp4Src: cdnUrl('fit-hier-homepage-header_1080p_v1.mp4'),
			webmSrc: cdnUrl('fit-hier-homepage-header_1080p_v1.webm'),
			posterImg: {
				src: '/img/fitness/small-group-training_01.jpg',
				alt: 'Fit Hier Personal Training',
				preload: true,
				width: 1920,
				height: 1490,
			},
		}"
		page-title-prefix="Personal Training"
		page-title="Privé training met 1 of 2 personen"
		:breadcrumbs
	>
		<div class="flex flex-col gap-36 pb-36 md:pt-64">
			<div class="flex flex-col gap-36 sticky">
				<div class="lg:container flex flex-col md:grid md:grid-cols-3 gap-12">
					<div>
						<div class="sticky top-36 flex flex-col gap-8 px-12 md:px-0">
							<AtomCard class="p-12 flex flex-col gap-12 rounded-lg">
								<div class="flex flex-col gap-4">
									<AtomTitle
										tag="h2"
										size="xl"
									>
										Personal Training
									</AtomTitle>
									<p class="text-functional-size-lg text-gray-600">
										Waarom Hier?
									</p>
								</div>
								<MoleculeIconList
									:items="[
										'De gym exclusief gereserveerd',
										'Voor 1 of 2 personen',
										'Volledig programma op maat',
										'Professionele coaching',
										'100% focus op jouw doelen',
										'Maximale progressie',
										'Flexibele planning in overleg',
										'Motiverende en veilige omgeving',
										'Meet & weeg je progressie',
										'Gratis handdoeken & koffiebar inbegrepen',
									]"
								/>
							</AtomCard>
							<AtomButton
								size="lg"
								text="Informatie aanvragen"
								:variant="ButtonVariant.Green"
								icon-left="fa-envelope"
								:to="{ url: '#aanmelden', title: 'Personal Training informatie aanvragen' }"
								@click="trackButtonClick('Page_PT_CTA_Lead')"
							/>
						</div>
					</div>
					<div class="relative col-span-2 grid gap-12">
						<div class="col-span-2 flex flex-col gap-12 bg-gray-50 md:rounded-lg p-12">
							<div class="relative flex flex-col gap-8">
								<AtomTitle
									tag="h2"
									size="2xl"
								>
									Waarom Personal Training?
								</AtomTitle>
								<p class="text-paragraph-size-lg">
									Niets werkt zo effectief als trainen met volledige focus en persoonlijke begeleiding. Personal Training bij Fit Hier is 1-op-1 of in duo, afgestemd op jouw doelen, tempo en niveau. Je krijgt maximale aandacht en werkt doelgericht aan progressie.
								</p>
							</div>
							<div class="relative flex flex-col gap-8">
								<AtomTitle
									tag="h3"
									size="xl"
									class="mt-8"
								>
									Voor wie?
								</AtomTitle>
								<p>
									Personal Training is ideaal voor wie bewust kiest voor kwaliteit, privacy en flexibiliteit. Je hebt een drukke agenda, duidelijke doelen en vindt het belangrijk om te trainen met persoonlijke begeleiding, voor maximaal resultaat. De gym is volledig voor jou gereserveerd. Geen afleiding, alleen focus, resultaat en persoonlijke aandacht.
								</p>
							</div>
							<div class="relative flex flex-col gap-8">
								<AtomTitle
									tag="h3"
									size="xl"
									class="mt-8"
								>
									Wat kun je verwachten?
								</AtomTitle>
								<p>
									Elke training duurt 60 minuten en vindt plaats in onze kleinschalige, moderne studio. Je werkt samen met je trainer aan:
								</p>
								<MoleculeIconList
									:items="[
										'Persoonlijke doelstellingen en voortgang',
										'Correcte houding, ademhaling en techniek',
										'Variatie in oefeningen en trainingsvormen',
										'Metingen en evaluaties',
										'Ondersteuning bij leefstijl en herstel',
									]"
								/>
								<p>Gratis gebruik van handdoeken, materialen en koffiebar is inbegrepen. Wij verzorgen alles wat je nodig hebt, jij richt je volledig op je training.</p>
							</div>
						</div>
					</div>
				</div>

				<div class="container grid grid-cols-1 md:grid-cols-2 gap-8">
					<AtomImage
						src="/img/fitness/squat-rack_01.jpg"
						alt="Personal Training"
						:width="640"
						:height="360"
						img-class="rounded aspect-video object-cover"
					/>
					<AtomImage
						src="/img/fitness/slee_04.jpg"
						alt="Personal Training"
						:width="640"
						:height="360"
						img-class="rounded aspect-video object-cover"
					/>
				</div>
			</div>

			<MoleculeBackgroundSection
				id="aanmelden"
				color="purple"
				class="lg:scroll-mt-72"
			>
				<div class="flex flex-col items-center gap-12">
					<AtomTitle
						tag="h2"
						size="3xl"
					>
						Personal Training informatiepakket
					</AtomTitle>
					<div class="lg:w-3/4 flex flex-col gap-12">
						<p class="text-center">
							Wil je meer weten over Personal Training of Duo Training bij Fit Hier? Vraag het informatiepakket aan en geef je voorkeur aan. Je ontvangt binnen enkele minuten alle details per mail.
						</p>
						<AtomCard class="p-12 flex flex-col gap-12 rounded-lg text-black">
							<OrganismFormLeadPt />
						</AtomCard>
					</div>
				</div>
			</MoleculeBackgroundSection>

			<div class="flex flex-col gap-64">
				<div class="flex flex-col gap-24 scroll-mt-36">
					<div class="container flex flex-col gap-20">
						<div class="flex flex-col gap-6">
							<div class="flex flex-col gap-2">
								<AtomTitle
									tag="h2"
									size="2xl"
								>
									Wat maakt onze Personal Training uniek?
								</AtomTitle>
								<p class=" text-gray-600 text-functional-size-lg">
									Een aanpak die werkt, omdat jij centraal staat.
								</p>
							</div>
							<p class="text-paragraph-size-lg">
								Bij Fit Hier draait alles om persoonlijke aandacht, maatwerk en een omgeving waarin jij echt vooruit kunt. Hieronder lees je wat onze aanpak zo effectief maakt.
							</p>
						</div>
					</div>
					<div class="container max-w-screen-2xl flex flex-col gap-12">
						<div class="grid grid-cols-1 md:grid-cols-3 gap-8">
							<AtomCard class="p-12 flex flex-col gap-8">
								<AtomTitle
									tag="h3"
									size="xl"
								>
									Persoonlijke aandacht en coaching
								</AtomTitle>
								<p>
									Bij Fit Hier draait het om persoonlijke aandacht. Je traint met een vaste coach die jouw traject volledig begeleidt en bijstuurt waar nodig. Dat betekent dat er niet alleen wordt gekeken naar de fysieke uitvoering, maar ook naar je mindset, motivatie en herstelmomenten. Zo zorgen we ervoor dat je progressie duurzaam is.
								</p>
							</AtomCard>
							<AtomCard class="p-12 flex flex-col gap-8">
								<AtomTitle
									tag="h3"
									size="xl"
								>
									Resultaatgericht en op maat
								</AtomTitle>
								<p>
									Je traint volledig op je eigen niveau, met een aanpak die past bij jouw doelen en levensstijl. Samen stellen we realistische doelen op en werken we stap voor stap aan het behalen ervan. Doordat je individueel of met z'n tweeën traint, heb je alle ruimte om vragen te stellen, technieken te verfijnen en het maximale uit jezelf te halen.
								</p>
							</AtomCard>
							<AtomCard class="p-12 flex flex-col gap-8">
								<AtomTitle
									tag="h3"
									size="xl"
								>
									Exclusieve trainingsomgeving
								</AtomTitle>
								<p>
									Onze studio is rustig en exclusief — geen afleiding van drukke toestellen of andere sporters. Je traint in een prettige, professionele omgeving met moderne materialen en alle ruimte om te focussen. Alles wordt geregeld: jij hoeft alleen te verschijnen en te knallen.
								</p>
							</AtomCard>
						</div>
					</div>
				</div>
			</div>

			<div class="container flex flex-col gap-32 md:gap-64 md:pb-32">
				<CompositionActionButtonsPt1 />
				<div class="flex flex-col gap-12 items-center">
					<AtomTitle
						tag="h2"
						size="3xl"
						class="text-center"
					>
						Onze leden aan het woord
					</AtomTitle>

					<OrganismReviewSlideshow :reviews="reviews" />
				</div>
			</div>

			<div class="container max-w-screen-md flex flex-col gap-12 pb-36">
				<AtomTitle
					tag="h2"
					size="3xl"
					class="text-center"
				>
					Veelgestelde vragen
				</AtomTitle>
				<OrganismFaq :items="ptFaqItems" />
			</div>
		</div>
	</NuxtLayout>
</template>

<script lang="ts" setup>
import { cdnUrl } from '#base/helpers/contentHelpers';
import reviews from '#fit/core/consts/reviews';
import { ptFaqItems } from '#fit/core/consts/faqItems';
import type { BreadcrumbsType } from '#base/core/types/BreadcrumbsType';
import { ButtonVariant } from '#base/core/enums/ButtonVariantEnum';

useSeoMeta({
	title: 'Personal Training Uden – Privé Coaching | Fit Hier',
	ogTitle: 'Personal Training bij Fit Hier – 1-op-1 Coaching in Uden',
	description: 'Privé personal training in Uden bij Fit Hier. Train 1-op-1 of in duo in een exclusieve studio, 100% aandacht voor jouw doelen. Boek een vrijblijvende kennismaking.',
});

const breadcrumbs: BreadcrumbsType = [
	{
		text: 'Home',
		to: {
			title: 'Home',
			url: '/',
		},
	},
	{
		text: 'Personal Training',
		to: {
			url: '/private-group-training',
			title: 'Personal Training',
		},
	},
];

const { trackButtonClick } = useEventTracking();
</script>
